/**
 * ============================================================
 * DNINE WARRANTY PAGE LOGIN GUARD
 * ============================================================
 *
 * Ensures users are logged in before accessing warranty page.
 * Provides seamless UX with auto-redirect and URL parameter preservation.
 *
 * Features:
 * - Login detection
 * - QR code parameter preservation
 * - Auto-redirect after login
 * - Customer data pre-filling
 * ============================================================
 */

class WarrantyPageGuard {
  constructor() {
    this.isCustomerLoggedIn = window.Shopify?.customer !== null && window.Shopify?.customer !== undefined;
    this.modal = null;
    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Check if we're on the warranty page
    if (!this.isWarrantyPage()) {
      return;
    }

    console.log('Warranty Page Guard: Initializing...');

    // Get the login modal
    this.modal = document.getElementById('login-required-modal');

    if (!this.modal) {
      console.error('Warranty Page Guard: Login modal not found');
      return;
    }

    // If user is NOT logged in, show login modal
    if (!this.isCustomerLoggedIn) {
      console.log('Warranty Page Guard: User not logged in, showing modal');
      this.hideFormAndShowModal();
      this.setupModalControls();
      this.trackLoginRequired();
    } else {
      console.log('Warranty Page Guard: User logged in, loading form');
      this.showForm();
      this.trackPageAccess();
    }
  }

  isWarrantyPage() {
    // Check if current page is /pages/warranty
    return window.location.pathname.includes('/pages/warranty') ||
      window.location.pathname.includes('/pages/product-warranty-registration');
  }

  hideFormAndShowModal() {
    // Hide the warranty form — support both old and new template classes
    var formContainer = document.querySelector('.dnine-warranty-section') || document.querySelector('.wr-page');
    if (formContainer) {
      formContainer.style.display = 'none';
    }
    // Also hide the hero banner if present
    var heroBanner = document.querySelector('.wr-hero');
    if (heroBanner) {
      heroBanner.style.display = 'none';
    }

    // Show login modal
    this.showModal();
  }

  showForm() {
    // Show the warranty form — support both old and new template classes
    var formContainer = document.querySelector('.dnine-warranty-section') || document.querySelector('.wr-page');
    if (formContainer) {
      formContainer.style.display = 'block';
    }
    var heroBanner = document.querySelector('.wr-hero');
    if (heroBanner) {
      heroBanner.style.display = 'block';
    }
  }

  showModal() {
    if (!this.modal) return;

    // Update modal content for warranty page
    this.updateModalContent();

    // Note: Close button has been removed from HTML (login is required)
    // No need to hide it via JavaScript anymore

    // Show modal with animation
    this.modal.style.display = 'flex';

    // Trigger animation
    requestAnimationFrame(() => {
      this.modal.classList.add('active');
    });

    // Prevent body scroll
    document.body.classList.add('login-modal-open');
    document.body.style.overflow = 'hidden';

    // Focus management for accessibility
    this.trapFocus();
  }

  updateModalContent() {
    // Update modal text to be warranty-specific
    const title = this.modal.querySelector('.login-modal-title');
    const description = this.modal.querySelector('.login-modal-description');
    const loginButton = this.modal.querySelector('a[href*="/account/login"]');

    if (title) {
      title.textContent = 'Login Required to Register Warranty';
    }

    if (description) {
      description.textContent = 'Please log in to your account to register your product warranty. This helps us verify your purchase and provide you with the best support.';
    }

    if (loginButton) {
      // Preserve current URL with parameters for return
      const currentUrl = window.location.href;
      const returnUrl = encodeURIComponent(currentUrl.replace(window.location.origin, ''));
      loginButton.href = `/account/login?return_url=${returnUrl}`;
    }
  }

  setupModalControls() {
    // When modal closes, restore the warranty form visibility
    var self = this;
    var closeBtn = document.getElementById('login-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function() { self.showForm(); });
    }
    // Also restore on overlay click and Escape key
    if (this.modal) {
      this.modal.addEventListener('click', function(e) {
        if (e.target === self.modal) self.showForm();
      });
    }
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') self.showForm();
    });
  }

  trapFocus() {
    const focusableElements = this.modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Store current focus to restore later
    this.previouslyFocusedElement = document.activeElement;

    // Focus first element
    firstElement.focus();

    // Trap focus within modal
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.modal.addEventListener('keydown', this.focusTrapHandler);
  }

  trackLoginRequired() {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'warranty_login_required', {
        event_category: 'warranty',
        event_label: 'User must login to access warranty page',
        page_location: window.location.href
      });
    }

    // Microsoft Clarity
    if (typeof clarity !== 'undefined') {
      clarity('set', 'warranty_login_required', 'shown');
    }
  }

  trackPageAccess() {
    // Track successful warranty page access
    if (typeof gtag !== 'undefined') {
      gtag('event', 'warranty_page_access', {
        event_category: 'warranty',
        event_label: 'User accessed warranty page',
        page_location: window.location.href,
        user_status: 'logged_in'
      });
    }

    // Microsoft Clarity
    if (typeof clarity !== 'undefined') {
      clarity('set', 'warranty_page_access', 'logged_in');
    }

    console.log('Warranty Page Guard: Tracked page access');
  }
}

// Initialize the guard
if (typeof window !== 'undefined') {
  window.warrantyPageGuard = new WarrantyPageGuard();
}
